import { v4 as uuidv4 } from 'uuid';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import type {
  SolutionTaskInput,
  SolutionVersionInput,
} from '@pypestream/api-services/urql';

import { proStudioService, sendProStudioEvent } from '../../xstate/app.xstate';
import { ProStudioContext } from '../../xstate/prostudio.xstate.types';
import { defaultRowData, NodeColumns, type TableRow } from '../table-store';
import {
  appendNumberingAnchorOf,
  numberingNumbersOf,
} from '../node-numbering-inputs';
import { assignNodeNumbers } from '../smart-node-numbering';

export interface Task {
  id: string;
  name: string;
  description?: string;
}

export interface CreateTaskNodeParams {
  taskName: string;
  taskDescription: string;
  taskId: string;
  solutionVersionId: string;
  existingRows: readonly TableRow[];
  order?: number;
  /**
   * PSTUDIO-4083: a number the CALLER has already allocated. One panel save can
   * create several task nodes, and that is ONE gesture, so its numbers must be
   * one contiguous block (rule 1.2) rather than N independent `count = 1`
   * allocations — which answered `5, 6, 7, 70` on a `1 · 8 · 2 · 4` table, a run
   * with a hole. It also skips the per-row collection walk, which is O(rows x
   * rich-asset size); see `numberingNumbersOf`. Omitted for a lone task node,
   * where a block of one is the same thing.
   */
  nodeNumber?: number;
}

/** The single-node allocation, kept here so `createTaskNode` reads the same
 *  whether or not the caller pre-allocated. A block of one through the shared
 *  allocator — not a separate rule. */
function allocateOneTaskNumber(existingRows: readonly TableRow[]): number {
  const anchor = appendNumberingAnchorOf(existingRows);
  const { own, referenced } = numberingNumbersOf(existingRows);
  return assignNodeNumbers(anchor, null, 1, own, referenced)[0];
}

/**
 * Creates a new task node with the specified parameters
 */
export function createTaskNode(params: CreateTaskNodeParams): TableRow {
  const {
    taskName,
    taskDescription,
    taskId,
    solutionVersionId,
    existingRows,
    order,
  } = params;

  // PSTUDIO-3992: a task node is APPENDED, so it is numbered from the last row
  // that carries a number — the same allocator every other append uses. The old
  // `Math.max(...existingRows.map(r => r.nodeNumber || 0)) + 1` counted group
  // headers (which hold no number by contract), skipped the positive-integer
  // check, and spread the entire row array through the call stack. This is a
  // REMOTE entrance too (syncTasksWithNodes), the one the entrance checklist
  // calls the most often forgotten.
  //
  // PSTUDIO-4083: both inputs come from the SHARED helpers the store's own
  // entrances use. They were hand-rolled here, which is how this entrance missed
  // the soft-delete window when the store's copies learned about it. Filtering
  // that window is the CALLER's job — `createTaskNodeWithId` passes
  // `tableStore.rowsForNumbering`.
  const taskNodeNumber =
    params.nodeNumber ?? allocateOneTaskNumber(existingRows);
  return {
    ...defaultRowData,
    [NodeColumns.nodeNumber]: taskNodeNumber,
    [NodeColumns.nodeType]: Tenancy_Node_Type_Enum_Ps_Enum.D,
    [NodeColumns.nodeName]: taskName,
    [NodeColumns.order]: order ?? 0,
    [NodeColumns.id]: uuidv4(),
    [NodeColumns.solutionVersionId]: solutionVersionId,
    [NodeColumns.utterances]: taskDescription,
    [NodeColumns.description]: '',
    [NodeColumns.settings]: {
      taskId,
    },
  };
}

/**
 * Updates a task node with new name and description
 */
export function updateTaskNode(
  taskNode: TableRow,
  updates: { name: string; description?: string }
): Partial<TableRow> {
  return {
    ...taskNode,
    [NodeColumns.nodeName]: updates.name,
    [NodeColumns.utterances]: updates.description,
  };
}

/**
 * Handles task creation response by updating the task node
 */
export function handleTaskCreationResponse(
  _taskNode: TableRow,
  taskDescription?: string
): Partial<TableRow> {
  return {
    [NodeColumns.utterances]: taskDescription,
  };
}

/**
 * Checks if a row is a task row based on the solution data
 */
export function isTaskRow(taskId: string): boolean {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;
  const isTask =
    draftVersionSolutionData?.tasks?.some((task) => task.id === taskId) ||
    false;
  return isTask;
}

type DraftVersion = NonNullable<ProStudioContext['draftVersionSolutionData']>;

/**
 * The `updateSolutionVersion` input for a task edit, built from the draft.
 *
 * Picks the `SolutionVersionInput` fields rather than spreading the draft: the
 * draft is a query result, and its read-only fields (`__typename`, `status`,
 * `name`, `version`, `isDraft`) are not input fields, which GraphQL rejects.
 * `solutionId` and `llmCredential` were already excluded for the same reason.
 *
 * Returns `undefined` when the draft carries no `files`: the mutation REPLACES
 * the version's files, so sending `[]` for a draft that merely failed to load
 * them would delete them. Callers skip the update instead.
 */
export function toSolutionVersionInput(
  draft: DraftVersion,
  tasks: SolutionTaskInput[]
): SolutionVersionInput | undefined {
  if (!draft.files) {
    console.warn('Draft has no files loaded; skipping the task update');
    return undefined;
  }
  return {
    botSolutionVersionId: draft.botSolutionVersionId,
    brandName: draft.brandName,
    knowledgeAgentContext: draft.knowledgeAgentContext,
    maxOutputTokens: draft.maxOutputTokens,
    otherInformation: draft.otherInformation,
    supervisorName: draft.supervisorName,
    temperature: draft.temperature,
    tone: draft.tone,
    topK: draft.topK,
    topP: draft.topP,
    tasks,
    files: draft.files.map((file) => ({
      url: file.url,
      mimeType: file.mimeType || '',
      originalName: file.originalName || '',
      publicId: file.publicId,
      size: file.size || 0,
    })),
  };
}

/**
 * Updates the solution data for a task row
 */
export function updateSolutionForTaskRow(
  taskId: string,
  updates: Partial<TableRow>
): void {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;

  if (!draftVersionSolutionData?.id) {
    console.warn('No solution version ID available for task update');
    return;
  }

  const task = draftVersionSolutionData.tasks?.find(
    (existingTask) => existingTask.id === taskId
  );
  if (!task) {
    console.warn('Task not found in solution data for row:', taskId);
    return;
  }

  // Determine what the new values would be and check if they've changed
  const currentName = task.name || '';
  const currentDescription = task.description || '';
  let hasChanges = false;
  let newName: string | undefined;
  let newDescription: string | undefined;

  if (updates.nodeName !== undefined) {
    newName = String(updates.nodeName);
    if (newName !== currentName) {
      hasChanges = true;
    }
  }

  if (updates.utterances !== undefined) {
    newDescription = String(updates.utterances);
    if (newDescription !== currentDescription) {
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return;
  }

  const finalName = newName !== undefined ? newName : currentName;
  const finalDescription =
    newDescription !== undefined ? newDescription : currentDescription;

  const version = {
    ...draftVersionSolutionData,
    solutionId: undefined,
    llmCredential: undefined,
    tasks:
      draftVersionSolutionData.tasks?.map((existingTask) =>
        existingTask.id === taskId
          ? {
              id: existingTask.id,
              name: finalName,
              description: finalDescription,
            }
          : {
              id: existingTask.id,
              name: existingTask.name || '',
              description: existingTask.description,
            }
      ) || [],
    files: draftVersionSolutionData.files?.map((file) => ({
      url: file.url,
      mimeType: file.mimeType || '',
      originalName: file.originalName || '',
      publicId: file.publicId,
      size: file.size || 0,
    })),
  };

  sendProStudioEvent({
    type: 'updateSolution',
    payload: {
      version,
      cb: () => {},
    },
  });
}

/**
 * Deletes a task from the solution data
 */
export function deleteSolutionTask(taskId: string): void {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;

  if (!draftVersionSolutionData?.id) {
    console.warn('No solution version ID available for task deletion');
    return;
  }

  const taskExists = draftVersionSolutionData.tasks?.some(
    (task) => task.id === taskId
  );

  if (!taskExists) {
    return;
  }

  const version = {
    ...draftVersionSolutionData,
    solutionId: undefined,
    llmCredential: undefined,
    tasks:
      draftVersionSolutionData.tasks
        ?.filter((task) => task.id !== taskId)
        .map((task) => ({
          id: task.id,
          name: task.name || '',
          description: task.description,
        })) || [],
    files: draftVersionSolutionData.files?.map((file) => ({
      url: file.url,
      mimeType: file.mimeType || '',
      originalName: file.originalName || '',
      publicId: file.publicId,
      size: file.size || 0,
    })),
  };

  sendProStudioEvent({
    type: 'updateSolution',
    payload: {
      version,
      cb: () => {},
    },
  });
}

/**
 * Gets task data from solution data
 */
export function getTasksFromSolutionData(
  updatedSolutionData?: ProStudioContext['draftVersionSolutionData']
): Task[] {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;
  const solutionData = updatedSolutionData || draftVersionSolutionData;

  if (!solutionData?.tasks || solutionData.tasks.length === 0) {
    return [];
  }

  return solutionData.tasks.map((task) => ({
    id: task.id || '',
    name: task.name || '',
    description: task.description,
  }));
}
